import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { GetSleepScreen } from '../../libs/GraphQL';
import SleepScreenVideoPreview from './SleepScreenVideoPreview';
import AssetAssignmentSection from './AssetAssignmentSection';
import { formatDate } from './sleepScreenHelpers';
import styles from './SleepScreenDetail.module.css';

const SleepScreenDetail = () => {
  const { sleepScreenId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const [entry, setEntry] = useState(null);
  const [status, setStatus] = useState('loading');
  const [bannerInfo, setBannerInfo] = useState(() => {
    if (location.state && location.state.justUpdated) {
      return { fieldsChanged: location.state.fieldsChanged };
    }
    return null;
  });
  // Force AssetAssignmentSection to bypass the per-entry TTL cache when the
  // user just returned from the assign flow — their writes need to show up.
  const [justAssigned] = useState(() =>
    Boolean(location.state && location.state.justAssigned),
  );

  useEffect(() => {
    if (
      location.state &&
      (location.state.justUpdated || location.state.justAssigned)
    ) {
      history.replace({ pathname: location.pathname, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchOne = async () => {
      setStatus('loading');
      try {
        const result = await GetSleepScreen(sleepScreenId);
        if (cancelled) return;
        if (!result) {
          setEntry(null);
          setStatus('notFound');
        } else {
          setEntry(result);
          setStatus('loaded');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('getSleepScreen failed:', err);
        toast.error('Failed to load sleep screen.');
        setStatus('error');
      }
    };
    fetchOne();
    return () => {
      cancelled = true;
    };
  }, [sleepScreenId]);

  if (status === 'loading') {
    return (
      <main className={styles.container}>
        <nav className={styles.breadcrumb} aria-label="Breadcrumb">
          <Link to="/sleep-screens" className={styles.crumbLink}>
            Sleep Screen Library
          </Link>
        </nav>
        <div className={styles.stateBlock}>Loading…</div>
      </main>
    );
  }

  if (status === 'notFound') {
    return (
      <main className={styles.container}>
        <nav className={styles.breadcrumb} aria-label="Breadcrumb">
          <Link to="/sleep-screens" className={styles.crumbLink}>
            Sleep Screen Library
          </Link>
          <span className={styles.crumbSep} aria-hidden="true">
            ›
          </span>
          <span className={styles.crumbCurrent}>Not found</span>
        </nav>
        <div className={styles.stateBlock}>
          No sleep screen with id <code>{sleepScreenId}</code> exists.
        </div>
      </main>
    );
  }

  if (status === 'error' || !entry) {
    return (
      <main className={styles.container}>
        <nav className={styles.breadcrumb} aria-label="Breadcrumb">
          <Link to="/sleep-screens" className={styles.crumbLink}>
            Sleep Screen Library
          </Link>
        </nav>
        <div className={styles.stateBlock}>Couldn’t load this entry.</div>
      </main>
    );
  }

  return (
    <main className={styles.container}>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/sleep-screens" className={styles.crumbLink}>
          Sleep Screen Library
        </Link>
        <span className={styles.crumbSep} aria-hidden="true">
          ›
        </span>
        <span className={styles.crumbCurrent}>
          {entry.displayName || entry.sleepScreenId}
        </span>
      </nav>

      {bannerInfo && (
        <div className={styles.successBanner}>
          <span className={styles.successIcon}>✓</span>
          <div className={styles.successContent}>
            <strong>Changes saved</strong>
            <span>
              Updated {bannerInfo.fieldsChanged}{' '}
              {bannerInfo.fieldsChanged === 1 ? 'field' : 'fields'} on{' '}
              {entry.displayName || entry.sleepScreenId}
            </span>
          </div>
          <button
            type="button"
            className={styles.bannerDismiss}
            onClick={() => setBannerInfo(null)}
            aria-label="dismiss"
          >
            ×
          </button>
        </div>
      )}

      <section className={styles.topSection}>
        <SleepScreenVideoPreview url={entry.videoUrl} />
        <div className={styles.topMeta}>
          <div className={styles.titleLabel}>Display name</div>
          <div className={styles.displayName}>{entry.displayName || '—'}</div>
          <dl className={styles.detailsGrid}>
            <dt>Catalog ID</dt>
            <dd className={styles.mono}>{entry.sleepScreenId}</dd>
            <dt>Description</dt>
            <dd>{entry.description || '—'}</dd>
            <dt>Video URL</dt>
            <dd className={styles.mono}>
              {entry.videoUrl ? (
                <a
                  href={entry.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {entry.videoUrl}
                </a>
              ) : (
                '—'
              )}
            </dd>
            <dt>Created on</dt>
            <dd>{formatDate(entry.createdAt, entry.createdBy)}</dd>
            <dt>Last edited on</dt>
            <dd>{formatDate(entry.updatedAt, entry.updatedBy)}</dd>
          </dl>
        </div>
        <div className={styles.headerActions}>
          <Link
            to={`/sleep-screens/assign?entry=${encodeURIComponent(
              entry.sleepScreenId,
            )}`}
            className={styles.assignButton}
          >
            assign fridges
          </Link>
          <Link
            to={`/sleep-screens/${encodeURIComponent(
              entry.sleepScreenId,
            )}/edit`}
            className={styles.editButton}
          >
            edit entry
          </Link>
        </div>
      </section>

      <AssetAssignmentSection
        sleepScreenId={entry.sleepScreenId}
        forceRefresh={justAssigned}
      />
    </main>
  );
};

export default SleepScreenDetail;
